import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer'
import { toOverrideCheckType } from '../../../lib/verification/overrides'
import type { CheckOverride, CheckResult, TotalsCheckResult, VerificationResult } from '../../../lib/verification/types'
import type { BatchRowResult } from './exportVerificationExcel'

Font.register({
  family: 'NotoSansSC',
  fonts: [
    { src: '/fonts/NotoSansSC-Regular.ttf', fontWeight: 'normal' },
    { src: '/fonts/NotoSansSC-Bold.ttf', fontWeight: 'bold' },
  ],
})

const FONT = 'NotoSansSC'
const BD = { borderWidth: 0.5, borderColor: '#cbd5e1' }

const styles = StyleSheet.create({
  page: { fontFamily: FONT, fontSize: 8, padding: 28 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  title: { fontSize: 14, fontWeight: 'bold' },
  meta: { fontSize: 8, color: '#475569', marginTop: 2 },
  badge: { fontSize: 10, fontWeight: 'bold', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4 },
  badgePass: { backgroundColor: '#dcfce7', color: '#15803d' },
  badgeFail: { backgroundColor: '#fee2e2', color: '#b91c1c' },
  section: { marginTop: 12 },
  sectionHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 },
  sectionTitle: { fontSize: 10, fontWeight: 'bold' },
  sectionSummary: { fontSize: 8, color: '#475569', marginBottom: 3 },
  statusTag: { fontSize: 8, fontWeight: 'bold', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 3 },
  tagPass: { backgroundColor: '#dcfce7', color: '#15803d' },
  tagFail: { backgroundColor: '#fee2e2', color: '#b91c1c' },
  tagNa: { backgroundColor: '#f1f5f9', color: '#475569' },
  table: { ...BD, borderStyle: 'solid' },
  tr: { flexDirection: 'row' },
  th: { ...BD, borderStyle: 'solid', padding: 3, fontWeight: 'bold', backgroundColor: '#f8fafc' },
  td: { ...BD, borderStyle: 'solid', padding: 3 },
  colLabel: { width: '30%' },
  colJob: { width: '25%' },
  colCi: { width: '25%' },
  colStatus: { width: '20%' },
  overrideBox: { marginTop: 4, padding: 4, borderRadius: 2, borderWidth: 0.5, borderStyle: 'solid' },
  overrideBoxAccepted: { backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' },
  overrideBoxRejected: { backgroundColor: '#fef2f2', borderColor: '#fecaca' },
  overrideTitle: { fontSize: 8, fontWeight: 'bold' },
  overrideMeta: { fontSize: 7, color: '#475569', marginTop: 1 },
  overrideNotes: { fontSize: 7, color: '#334155', marginTop: 2 },
})

function OverrideBox({ override }: { override: CheckOverride }) {
  const accepted = override.overrideStatus === 'accepted'
  return (
    <View style={[styles.overrideBox, accepted ? styles.overrideBoxAccepted : styles.overrideBoxRejected]}>
      <Text style={styles.overrideTitle}>Staff Override: {override.overrideStatus.toUpperCase()} (final decision)</Text>
      <Text style={styles.overrideMeta}>{override.staffEmail} — {new Date(override.createdAt).toLocaleString()}</Text>
      <Text style={styles.overrideNotes}>{override.notes}</Text>
    </View>
  )
}

function statusTagStyle(status: 'pass' | 'fail' | 'na' | 'match' | 'mismatch') {
  if (status === 'pass' || status === 'match') return styles.tagPass
  if (status === 'na') return styles.tagNa
  return styles.tagFail
}

function CheckSection({ check, override }: { check: CheckResult; override?: CheckOverride }) {
  return (
    <View style={styles.section} wrap>
      <View style={styles.sectionHeaderRow}>
        <Text style={styles.sectionTitle}>{check.title}</Text>
        <Text style={[styles.statusTag, statusTagStyle(check.status)]}>{check.status.toUpperCase()}</Text>
      </View>
      <Text style={styles.sectionSummary}>{check.summary}</Text>
      {check.rows.length > 0 && (
        <View style={styles.table}>
          <View style={styles.tr}>
            <Text style={[styles.th, styles.colLabel]}>Item</Text>
            <Text style={[styles.th, styles.colJob]}>Job Value</Text>
            <Text style={[styles.th, styles.colCi]}>CI Value</Text>
            <Text style={[styles.th, styles.colStatus]}>Status</Text>
          </View>
          {check.rows.map((row, idx) => (
            <View style={styles.tr} key={idx} wrap={false}>
              <Text style={[styles.td, styles.colLabel]}>{row.label}</Text>
              <Text style={[styles.td, styles.colJob]}>{row.jobValue ?? '-'}</Text>
              <Text style={[styles.td, styles.colCi]}>{row.ciValue ?? '-'}</Text>
              <Text style={[styles.td, styles.colStatus]}>{row.status}{row.note ? ` (${row.note})` : ''}</Text>
            </View>
          ))}
        </View>
      )}
      {override && <OverrideBox override={override} />}
    </View>
  )
}

function TotalsSection({ check, override }: { check: TotalsCheckResult; override?: CheckOverride }) {
  return (
    <View style={styles.section} wrap>
      <View style={styles.sectionHeaderRow}>
        <Text style={styles.sectionTitle}>{check.title}</Text>
        <Text style={[styles.statusTag, statusTagStyle(check.status)]}>{check.status.toUpperCase()}</Text>
      </View>
      <Text style={styles.sectionSummary}>{check.summary}</Text>
      <View style={styles.table}>
        <View style={styles.tr}>
          <Text style={[styles.th, { width: '40%' }]}>Metric</Text>
          <Text style={[styles.th, { width: '20%' }]}>Job</Text>
          <Text style={[styles.th, { width: '20%' }]}>CI</Text>
          <Text style={[styles.th, { width: '20%' }]}>Status</Text>
        </View>
        <View style={styles.tr}>
          <Text style={[styles.td, { width: '40%' }]}>Packages</Text>
          <Text style={[styles.td, { width: '20%' }]}>{check.packages.jobValue}</Text>
          <Text style={[styles.td, { width: '20%' }]}>{check.packages.ciValue}</Text>
          <Text style={[styles.td, { width: '20%' }]}>{check.packages.status}</Text>
        </View>
        <View style={styles.tr}>
          <Text style={[styles.td, { width: '40%' }]}>Gross Weight (kg)</Text>
          <Text style={[styles.td, { width: '20%' }]}>{check.grossWeight.jobValue.toFixed(3)}</Text>
          <Text style={[styles.td, { width: '20%' }]}>{check.grossWeight.ciValue.toFixed(3)}</Text>
          <Text style={[styles.td, { width: '20%' }]}>{check.grossWeight.status}</Text>
        </View>
        <View style={styles.tr}>
          <Text style={[styles.td, { width: '40%' }]}>CBM (m³)</Text>
          <Text style={[styles.td, { width: '20%' }]}>{check.cbm.jobValue != null ? check.cbm.jobValue.toFixed(3) : '-'}</Text>
          <Text style={[styles.td, { width: '20%' }]}>{check.cbm.ciValue != null ? check.cbm.ciValue.toFixed(3) : '-'}</Text>
          <Text style={[styles.td, { width: '20%' }]}>{check.cbm.status === 'na' ? check.cbm.note : check.cbm.status}</Text>
        </View>
      </View>
      {override && <OverrideBox override={override} />}
    </View>
  )
}

function JobReportPage({ result, overrides }: { result: VerificationResult; overrides: CheckOverride[] }) {
  const overallPass = !result.noInvoicesFound && result.failCount === 0
  const overrideByCheckType = new Map(overrides.map(o => [o.checkType, o]))
  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.headerRow}>
        <View>
          <Text style={styles.title}>Three-Way Verification Report</Text>
          <Text style={styles.meta}>Job Ref: {result.jobRef}</Text>
          <Text style={styles.meta}>Verified: {new Date(result.verifiedAt).toLocaleString()} by {result.verifiedBy}</Text>
          <Text style={styles.meta}>CI file(s): {result.cisIncluded.map(c => c.parsed.header.invoice_no ?? c.fileName).join(', ') || 'None'}</Text>
          {result.consol && (
            <Text style={styles.meta}>Consol bundle: parent {result.consol.parentJobRef} + children {result.consol.childJobRefs.join(', ')}</Text>
          )}
        </View>
        <Text style={[styles.badge, overallPass ? styles.badgePass : styles.badgeFail]}>
          {result.noInvoicesFound ? 'NO CI DATA' : overallPass ? 'ALL PASS' : `${result.failCount} FAILED`}
        </Text>
      </View>

      {result.checks.map(check =>
        check.id === 'totals'
          ? <TotalsSection key={check.id} check={check as TotalsCheckResult} override={overrideByCheckType.get('totals')} />
          : <CheckSection key={check.id} check={check as CheckResult} override={overrideByCheckType.get(toOverrideCheckType(check.id))} />
      )}
    </Page>
  )
}

export default function VerificationReportPDF({ rows }: { rows: BatchRowResult[] }) {
  return (
    <Document>
      {rows.map(({ result, overrides }) => (
        <JobReportPage key={result.jobRef} result={result} overrides={overrides} />
      ))}
    </Document>
  )
}
